import { AMOUNT_TYPE, ITEM_TYPE } from '@/utils/enum';
import { unMaskPrice } from '@/utils/common';

/**
 * Đồng bộ với BillProduct / BillRefundProduct: làm tròn tiền đến hàng nghìn.
 */
export function roundMoneyToThousand(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / 1000) * 1000;
}

/** GET voucher detail: CSV string | null | undefined */
export function parseStackableDiscountProgramIdsFromApi(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.map(Number).filter((x) => Number.isFinite(x) && x > 0);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((x) => Number.isFinite(x) && x > 0);
  }
  return [];
}

export function serializeStackableDiscountProgramIdsForApi(ids) {
  if (!Array.isArray(ids) || !ids.length) return '';
  return [...new Set(ids.map(Number).filter((x) => Number.isFinite(x) && x > 0))]
    .sort((a, b) => a - b)
    .join(',');
}

export function hasVoucherOnLine(el) {
  const code = el.voucherCode != null ? String(el.voucherCode).trim() : '';
  return !!(code || el.voucherId || el.voucherDetailId);
}

export function isStackedDiscountLine(el) {
  const pid = Number(el.discountProgramId);
  return Number.isFinite(pid) && pid > 0 && hasVoucherOnLine(el);
}

/** Chỉ dòng MAIN không gán parent — không tách CK cho gift/attach/flex child */
export function isMainOrderLineForStackPayload(el) {
  return Number(el.orderItemType) === ITEM_TYPE.MAIN && !el.belongOrderDetailId;
}

export function isMainBillLineForStackPayload(el) {
  return Number(el.itemType) === ITEM_TYPE.MAIN && !el.belongBillDetailId;
}

/**
 * Chiết khấu đơn vị (tiền) từ discountAmount + discountType — cùng quy ước calculate.mixins.calculateTotalAmountOrderItems.
 */
export function perUnitLineDiscountMoney(line) {
  const unit = Number(line.sellingPriceMask ?? line.sellingPrice ?? line.productPrice ?? 0);
  const raw = unMaskPrice(line.discountAmount ?? 0);
  const disc = Number(raw);
  const dtype = Number(line.discountType ?? AMOUNT_TYPE.MONEY);
  if (!Number.isFinite(unit)) return 0;
  if (dtype === AMOUNT_TYPE.PERCENT) {
    return (unit * disc) / 100;
  }
  return Number.isFinite(disc) ? disc : 0;
}

export function rawTotalLineDiscountMoney(line) {
  const qty = Number(line.quantity ?? 1) || 1;
  return perUnitLineDiscountMoney(line) * qty;
}

export function totalLineDiscountMoneyRounded(line) {
  return roundMoneyToThousand(rawTotalLineDiscountMoney(line));
}

/**
 * Chỉ phần CTGG trên dòng — không gồm voucher (tránh double-count khi apply voucher thứ 2).
 */
export function programOnlyLineDiscountRounded(line) {
  const programField = roundMoneyToThousand(Number(line.discountProgramAmount) || 0);
  if (programField > 0) {
    return programField;
  }
  const pid = Number(line.discountProgramId);
  if (!Number.isFinite(pid) || pid <= 0) {
    return 0;
  }
  const lineTotal = totalLineDiscountMoneyRounded(line);
  const voucherPart = roundMoneyToThousand(Number(line.voucherAmount) || 0);
  if (voucherPart > 0 && lineTotal >= voucherPart) {
    return roundMoneyToThousand(lineTotal - voucherPart);
  }
  return lineTotal;
}

/**
 * Gán tổng CK dòng = program + voucher (đã round từng phần), đồng bộ discountAmount kiểu tiền trên đơn vị.
 */
export function applyStackedAmountsToLineDisplayFields(line, discountProgramAmount, voucherAmount) {
  const qty = Number(line.quantity ?? 1) || 1;
  const p = roundMoneyToThousand(discountProgramAmount);
  const v = roundMoneyToThousand(voucherAmount);
  const total = roundMoneyToThousand(p + v);
  line.discountProgramAmount = p;
  line.voucherAmount = v;
  line.discountType = AMOUNT_TYPE.MONEY;
  line.discountAmount = qty > 0 ? total / qty : 0;
}

/** Khi chỉ có CTGG (chưa voucher): lưu toàn bộ CK dòng vào discountProgramAmount */
export function setProgramOnlySplitAmounts(line) {
  const total = totalLineDiscountMoneyRounded(line);
  line.discountProgramAmount = total;
  line.voucherAmount = 0;
}

/** Khi chỉ có voucher (không program): không dùng tách trong payload stacked */
export function clearSplitAmountFields(line) {
  line.discountProgramAmount = 0;
  line.voucherAmount = 0;
}

/** Hydrate từ API đơn/hóa đơn — chỉ gán khi có số liệu */
export function hydrateSplitAmountsFromApi(line, discountProgramAmount, voucherAmount) {
  const pa = Number(discountProgramAmount);
  const va = Number(voucherAmount);
  const hasPa = Number.isFinite(pa);
  const hasVa = Number.isFinite(va);
  if (!hasPa && !hasVa) {
    clearSplitAmountFields(line);
    return;
  }
  line.discountProgramAmount = hasPa ? pa : 0;
  line.voucherAmount = hasVa ? va : 0;
}

export function appendStackAmountsIfNeeded(payloadItem, billOrOrderLineItem) {
  if (
    payloadItem &&
    billOrOrderLineItem &&
    isMainBillLineForStackPayload(billOrOrderLineItem) &&
    isStackedDiscountLine(billOrOrderLineItem)
  ) {
    payloadItem.discountProgramAmount = Number(
      billOrOrderLineItem.discountProgramAmount ?? 0,
    );
    payloadItem.voucherAmount = Number(billOrOrderLineItem.voucherAmount ?? 0);
  }
  return payloadItem;
}
